/**
 * Broker Analysis Component
 * Tabbed layout: Summary | Top Brokers | Lane Analysis
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../utils/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ComposedChart, Line, Cell, CartesianGrid, Legend,
  PieChart, Pie
} from 'recharts';
import { CHART_COLORS, SERIES_PALETTE } from '../utils/chartTheme';
import '../styles/BrokerAnalysis.css';
import '../styles/Charts.css';

function BrokerAnalysis({ selectedCompany = '', buildParams: parentBuildParams, filterOptions = { dispatchers: [], brokers: [], drivers: [] }, selectedWeeks = [], filterVersion = 0 }) {
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [laneData, setLaneData] = useState(null);

  // Entity-specific filters (kept local)
  const [brokerFilter, setBrokerFilter] = useState('');
  const [dispatcherFilter, setDispatcherFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');

  // View state
  const [activeSection, setActiveSection] = useState('summary');
  const [summaryView, setSummaryView] = useState('table'); // 'table' | 'charts'
  const [expandedBroker, setExpandedBroker] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Sorting — client-side for summary/lanes, server-side for details
  const [summarySort, setSummarySort] = useState({ key: 'total_pay', dir: 'desc' });
  const [laneSort, setLaneSort] = useState({ key: 'rpm', dir: 'desc' });
  const [detailSort, setDetailSort] = useState({ key: null, dir: 'desc' });

  // Detail loads
  const [detailLoads, setDetailLoads] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);

  // Lane drill-down (filters detail view to a specific lane)
  const [selectedLane, setSelectedLane] = useState(null);

  // Track if initial load done for this company
  const [loadedCompany, setLoadedCompany] = useState(null);

  useEffect(() => {
    if (selectedCompany && selectedCompany !== loadedCompany) {
      setLoadedCompany(selectedCompany);
      setBrokerFilter('');
      setDispatcherFilter('');
      setDriverFilter('');
      loadData();
    }
  }, [selectedCompany]);

  // Re-fetch when shared filters change (filterVersion bumped by Dashboard)
  useEffect(() => {
    if (selectedCompany && loadedCompany && filterVersion > 0) {
      loadData();
      if (activeSection === 'details') loadDetailLoads();
    }
  }, [filterVersion]);

  // Real-time fetch on entity dropdown filter change
  useEffect(() => {
    if (selectedCompany && loadedCompany) {
      loadData();
      if (activeSection === 'details') loadDetailLoads();
    }
  }, [brokerFilter, dispatcherFilter, driverFilter]);

  useEffect(() => {
    if (selectedCompany && activeSection === 'details') {
      loadDetailLoads();
    }
  }, [detailPage, detailSort]);

  const buildParams = useCallback(() => {
    const extra = {};
    if (brokerFilter) extra.broker = brokerFilter;
    if (dispatcherFilter) extra.dispatcher = dispatcherFilter;
    if (driverFilter) extra.driver_name = driverFilter;
    return parentBuildParams(extra);
  }, [parentBuildParams, brokerFilter, dispatcherFilter, driverFilter]);

  const loadData = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const qs = buildParams();
      const suffix = qs ? '?' + qs : '';
      const [brokerRes, laneRes] = await Promise.all([
        api.get(`/loads/${selectedCompany}/broker-summary${suffix}`),
        api.get(`/loads/${selectedCompany}/lane-summary${suffix}`),
      ]);
      setSummaryData(brokerRes.data);
      setLaneData(laneRes.data);
    } catch (error) {
      console.error('Failed to load broker data:', error);
      setSummaryData(null);
      setLaneData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailLoads = async (laneOverride) => {
    if (!selectedCompany) return;
    setDetailLoading(true);
    try {
      const lane = laneOverride !== undefined ? laneOverride : selectedLane;
      // Build base params — skip broker if lane filter is active (lane has its own broker)
      const extra = {};
      if (!lane && brokerFilter) extra.broker = brokerFilter;
      if (dispatcherFilter) extra.dispatcher = dispatcherFilter;
      if (driverFilter) extra.driver_name = driverFilter;
      const qs = parentBuildParams(extra);
      const sep = qs ? '&' : '';
      let sortQs = '';
      if (detailSort.key) {
        sortQs = `&sort_by=${detailSort.key}&sort_dir=${detailSort.dir}`;
      }
      let laneQs = '';
      if (lane) {
        laneQs = `&broker=${encodeURIComponent(lane.broker)}&pickup_state=${encodeURIComponent(lane.pickup_state)}&delivery_state=${encodeURIComponent(lane.delivery_state)}`;
      }
      const url = `/loads/${selectedCompany}?page=${detailPage}&limit=50${sep}${qs}${sortQs}${laneQs}`;
      const response = await api.get(url);
      setDetailLoads(response.data);
    } catch (error) {
      console.error('Failed to load detail:', error);
      setDetailLoads(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleLaneClick = (lane) => {
    setSelectedLane(lane);
    setDetailPage(1);
    setActiveSection('details');
    loadDetailLoads(lane);
  };

  const clearLaneFilter = () => {
    setSelectedLane(null);
    setDetailPage(1);
    loadDetailLoads(null);
  };

  const handleClearEntityFilters = () => {
    setBrokerFilter('');
    setDispatcherFilter('');
    setDriverFilter('');
    setSelectedLane(null);
    setDetailPage(1);
  };

  // --- Sorting helpers ---
  const toggleSort = (setter) => (key) => {
    setter(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const toggleDetailSort = (key) => {
    setDetailSort(prev => {
      const newDir = prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc';
      return { key, dir: newDir };
    });
    setDetailPage(1);
  };

  const sortIndicator = (sortState, key) => {
    if (sortState.key !== key) return <span className="ba-sort-icon ba-sort-inactive">{'\u2195'}</span>;
    return <span className="ba-sort-icon">{sortState.dir === 'desc' ? '\u25BC' : '\u25B2'}</span>;
  };

  const clientSort = (arr, sortState) => {
    if (!sortState.key || !arr?.length) return arr || [];
    const sorted = [...arr];
    sorted.sort((a, b) => {
      let va = a[sortState.key], vb = b[sortState.key];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortState.dir === 'asc' ? -1 : 1;
      if (va > vb) return sortState.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const sortedSummary = useMemo(() => clientSort(summaryData?.summary, summarySort), [summaryData?.summary, summarySort]);
  const sortedLanes = useMemo(() => clientSort(summaryData?.lanes, laneSort), [summaryData?.lanes, laneSort]);

  const getTierClass = (rpm, dh) => {
    if (rpm >= 2.5 && dh <= 15) return 'ba-tier-green';
    if (rpm >= 2.0 || dh <= 25) return 'ba-tier-yellow';
    return 'ba-tier-red';
  };

  // Chart data — top 20 brokers by revenue
  const top20Brokers = useMemo(() => {
    if (!summaryData?.summary) return [];
    return [...summaryData.summary]
      .sort((a, b) => (b.total_pay || 0) - (a.total_pay || 0))
      .slice(0, 20)
      .reverse();
  }, [summaryData]);

  const hasData = summaryData?.summary?.length > 0;

  // Filter weekly_trends by selected weeks from Dashboard
  const filteredTrends = useMemo(() => {
    if (!summaryData?.weekly_trends) return [];
    if (!selectedWeeks.length) return summaryData.weekly_trends;
    return summaryData.weekly_trends.filter(t => selectedWeeks.includes(t.week_start));
  }, [summaryData, selectedWeeks]);
  const maxBrokerPay = useMemo(() => {
    if (!top20Brokers.length) return 1;
    return Math.max(...top20Brokers.map(b => b.total_pay || 0), 1);
  }, [top20Brokers]);

  // Scatter data — RPM vs load count
  const scatterData = useMemo(() => {
    if (!summaryData?.summary) return [];
    return summaryData.summary.map(b => ({
      broker: b.broker,
      load_count: b.load_count || 0,
      rpm: b.rpm || 0,
      total_pay: b.total_pay || 0,
    }));
  }, [summaryData]);

  // Concentration risk — top 5 + Others
  const concentrationData = useMemo(() => {
    if (!summaryData?.summary) return [];
    const sorted = [...summaryData.summary].sort((a, b) => (b.total_pay || 0) - (a.total_pay || 0));
    const total = sorted.reduce((sum, b) => sum + (b.total_pay || 0), 0);
    const top5 = sorted.slice(0, 5).map(b => ({
      name: b.broker,
      value: b.total_pay || 0,
      pct: total > 0 ? ((b.total_pay || 0) / total * 100) : 0,
    }));
    const othersValue = sorted.slice(5).reduce((sum, b) => sum + (b.total_pay || 0), 0);
    if (othersValue > 0) {
      top5.push({ name: 'Others', value: othersValue, pct: total > 0 ? (othersValue / total * 100) : 0 });
    }
    return top5;
  }, [summaryData]);

  // --- Formatters ---
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);
  };

  const formatNumber = (val, decimals = 0) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(val || 0);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  const getLanesForBroker = (brokerName) => {
    if (!summaryData?.lanes) return [];
    return summaryData.lanes.filter(l => l.broker === brokerName).sort((a, b) => b.rpm - a.rpm).slice(0, 10);
  };

  // --- Weekly helpers ---
  const formatWeekLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatWeekRange = (dateStr) => {
    const start = new Date(dateStr + 'T00:00:00');
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${s} - ${e}`;
  };

  const getRpmClass = (rpm) => {
    if (rpm >= 3.0) return 'rpm-good';
    if (rpm >= 2.0) return 'rpm-ok';
    if (rpm >= 1.5) return 'rpm-low';
    return 'rpm-bad';
  };

  const getHeatClass = (pay, maxPay) => {
    if (!maxPay || !pay) return '';
    const ratio = pay / maxPay;
    if (ratio >= 0.8) return 'heat-green-strong';
    if (ratio >= 0.5) return 'heat-green';
    if (ratio >= 0.3) return 'heat-blue';
    if (ratio >= 0.15) return 'heat-yellow';
    return 'heat-red';
  };

  const getBarFillClass = (rpm) => {
    if (rpm >= 3.0) return 'fill-green';
    if (rpm >= 2.0) return 'fill-blue';
    if (rpm >= 1.5) return 'fill-yellow';
    return 'fill-red';
  };

  const getDhClass = (dh) => {
    if (dh <= 10) return 'dh-low';
    if (dh <= 15) return 'dh-med';
    return 'dh-high';
  };

  const getWeeklyGrid = () => {
    if (!filteredTrends.length) return null;
    const weeks = [...new Set(filteredTrends.map(t => t.week_start))].sort();
    const brokers = [...new Set(filteredTrends.map(t => t.broker))];
    const lookup = {};
    filteredTrends.forEach(t => { lookup[`${t.broker}|${t.week_start}`] = t; });

    const maxPayPerWeek = {};
    weeks.forEach(w => {
      let max = 0;
      brokers.forEach(b => {
        const cell = lookup[`${b}|${w}`];
        if (cell && cell.total_pay > max) max = cell.total_pay;
      });
      maxPayPerWeek[w] = max;
    });

    const globalMaxPay = Math.max(...filteredTrends.map(t => t.total_pay || 0), 1);

    const weekTotals = {};
    weeks.forEach(w => {
      let totalPay = 0, totalLoads = 0, totalMiles = 0;
      brokers.forEach(b => {
        const cell = lookup[`${b}|${w}`];
        if (cell) {
          totalPay += cell.total_pay || 0;
          totalLoads += cell.load_count || 0;
          totalMiles += cell.total_miles || 0;
        }
      });
      weekTotals[w] = { total_pay: totalPay, load_count: totalLoads, total_miles: totalMiles, rpm: totalMiles > 0 ? totalPay / totalMiles : 0 };
    });

    const brokerTotals = {};
    brokers.forEach(b => {
      let totalPay = 0, totalLoads = 0, totalMiles = 0;
      weeks.forEach(w => {
        const cell = lookup[`${b}|${w}`];
        if (cell) {
          totalPay += cell.total_pay || 0;
          totalLoads += cell.load_count || 0;
          totalMiles += cell.total_miles || 0;
        }
      });
      brokerTotals[b] = { total_pay: totalPay, load_count: totalLoads, rpm: totalMiles > 0 ? totalPay / totalMiles : 0 };
    });

    const grandTotal = { total_pay: 0, load_count: 0, total_miles: 0, rpm: 0 };
    Object.values(weekTotals).forEach(wt => {
      grandTotal.total_pay += wt.total_pay;
      grandTotal.load_count += wt.load_count;
      grandTotal.total_miles += wt.total_miles;
    });
    grandTotal.rpm = grandTotal.total_miles > 0 ? grandTotal.total_pay / grandTotal.total_miles : 0;

    return { weeks, brokers, lookup, maxPayPerWeek, globalMaxPay, weekTotals, brokerTotals, grandTotal };
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      if (summaryData?.summary) {
        const rows = summaryData.summary.map(b => ({
          'Broker': b.broker, 'Load Count': b.load_count, 'Total Pay': b.total_pay, 'Avg/Load': b.avg_pay_per_load,
          'Total Miles': b.total_miles, 'Empty Miles': b.total_empty_miles, 'RPM': b.rpm, 'Deadhead %': b.deadhead_pct,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Broker Summary');
      }

      if (summaryData?.lanes?.length > 0) {
        const laneRows = summaryData.lanes.map(l => ({
          'Broker': l.broker, 'Pickup State': l.pickup_state, 'Delivery State': l.delivery_state,
          'Lane Count': l.lane_count, 'Avg Pay': l.avg_pay, 'Avg Miles': l.avg_miles, 'RPM': l.rpm,
        }));
        const ws2 = XLSX.utils.json_to_sheet(laneRows);
        ws2['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 8 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Lane Analysis');
      }

      XLSX.writeFile(wb, `broker_analysis_${selectedCompany}.xlsx`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const SortTh = ({ sort, onSort, sortKey, children, className = '' }) => (
    <th className={`ba-sortable ${className}`} onClick={() => onSort(sortKey)}>
      {children} {sortIndicator(sort, sortKey)}
    </th>
  );

  const DetailSortTh = ({ sortKey, children, className = '' }) => (
    <th className={`ba-sortable ${className}`} onClick={() => toggleDetailSort(sortKey)}>
      {children} {sortIndicator(detailSort, sortKey)}
    </th>
  );

  return (
    <div className="broker-analysis">
      {/* Controls */}
      {/* Entity Filters */}
      <div className="ba-controls">
        <div className="ba-filters">
          <div className="ba-filter-group">
            <label>Broker:</label>
            <select value={brokerFilter} onChange={e => setBrokerFilter(e.target.value)} className="ba-filter-select">
              <option value="">All Brokers</option>
              {filterOptions.brokers.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="ba-filter-group">
            <label>Dispatcher:</label>
            <select value={dispatcherFilter} onChange={e => setDispatcherFilter(e.target.value)} className="ba-filter-select">
              <option value="">All Dispatchers</option>
              {filterOptions.dispatchers.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="ba-filter-group">
            <label>Driver:</label>
            <select value={driverFilter} onChange={e => setDriverFilter(e.target.value)} className="ba-filter-select">
              <option value="">All Drivers</option>
              {filterOptions.drivers.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          {(brokerFilter || dispatcherFilter || driverFilter) && (
            <button onClick={handleClearEntityFilters} className="btn-secondary btn-small">Clear Filters</button>
          )}
          {hasData && (
            <button onClick={handleExport} className="btn-secondary btn-small" disabled={exporting} style={{ marginLeft: 'auto' }}>
              {exporting ? 'Exporting...' : 'Export Excel'}
            </button>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      {selectedCompany && !loading && hasData && (
        <div className="ba-tabs">
          {['summary', 'lanes', 'details'].map(s => (
            <button key={s} className={`ba-tab ${activeSection === s ? 'active' : ''}`} onClick={() => setActiveSection(s)}>
              {s === 'summary' ? 'Broker Summary' : s === 'lanes' ? 'Lane Analysis' : 'Load Details'}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="ba-loading">
          <div className="dashboard-spinner"></div>
          <p>Loading broker data...</p>
        </div>
      ) : !selectedCompany ? (
        <div className="ba-empty">Select a company to view broker analysis.</div>
      ) : !hasData ? (
        <div className="ba-empty">No broker data found. Upload load data in the Admin panel.</div>
      ) : (
        <>
          {/* Broker Summary */}
          {activeSection === 'summary' && (
            !summaryData?.summary?.length ? (
              <div className="ba-empty">No broker data found. Upload load data in the Admin panel.</div>
            ) : (
              <>
              <div className="ba-view-toggle">
                <button className={`ba-section-tab ${summaryView === 'table' ? 'active' : ''}`} onClick={() => setSummaryView('table')}>Table</button>
                <button className={`ba-section-tab ${summaryView === 'charts' ? 'active' : ''}`} onClick={() => setSummaryView('charts')}>Charts</button>
              </div>

              {summaryView === 'table' && (
              <div className="ba-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="broker">Broker</SortTh>
                      <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="load_count" className="text-right">Loads</SortTh>
                      <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="total_pay" className="text-right">Total Pay</SortTh>
                      <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="avg_pay_per_load" className="text-right">Avg/Load</SortTh>
                      <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="total_miles" className="text-right">Total Miles</SortTh>
                      <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="total_empty_miles" className="text-right">Empty Mi</SortTh>
                      <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="rpm" className="text-right">RPM</SortTh>
                      <SortTh sort={summarySort} onSort={toggleSort(setSummarySort)} sortKey="deadhead_pct" className="text-right">DH %</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSummary.map((b, i) => {
                      const isExpanded = expandedBroker === b.broker;
                      const lanes = getLanesForBroker(b.broker);
                      const tierClass = getTierClass(b.rpm, b.deadhead_pct);
                      return (
                        <tr key={i} className={`${tierClass} ${isExpanded ? 'ba-row-expanded' : ''}`}>
                          <td>
                            <button className="ba-broker-name" onClick={() => setExpandedBroker(isExpanded ? null : b.broker)}>
                              {b.broker}
                              {lanes.length > 0 && <span className="ba-expand-icon">{isExpanded ? ' -' : ' +'}</span>}
                            </button>
                            {isExpanded && lanes.length > 0 && (
                              <div className="ba-lanes-inline">
                                <table className="ba-lane-table">
                                  <thead>
                                    <tr>
                                      <th>Lane</th>
                                      <th className="text-right">Count</th>
                                      <th className="text-right">Avg Pay</th>
                                      <th className="text-right">Avg Miles</th>
                                      <th className="text-right">RPM</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {lanes.map((l, li) => (
                                      <tr key={li} className="ba-lane-row" onClick={() => handleLaneClick(l)} title="Click to view loads">
                                        <td>{l.pickup_state} &rarr; {l.delivery_state}</td>
                                        <td className="text-right">{l.lane_count}</td>
                                        <td className="text-right">{formatCurrency(l.avg_pay)}</td>
                                        <td className="text-right">{formatNumber(l.avg_miles)}</td>
                                        <td className="text-right">
                                          <span className={`ba-rpm-badge ${l.rpm >= 3 ? 'rpm-good' : l.rpm >= 2 ? 'rpm-ok' : l.rpm >= 1.5 ? 'rpm-low' : 'rpm-bad'}`}>
                                            ${formatNumber(l.rpm, 2)}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                          <td className="text-right">{formatNumber(b.load_count)}</td>
                          <td className="text-right">{formatCurrency(b.total_pay)}</td>
                          <td className="text-right">{formatCurrency(b.avg_pay_per_load)}</td>
                          <td className="text-right">{formatNumber(b.total_miles)}</td>
                          <td className="text-right">{formatNumber(b.total_empty_miles)}</td>
                          <td className="text-right ba-rpm">${formatNumber(b.rpm, 2)}</td>
                          <td className="text-right">
                            <span className={`ba-deadhead ${b.deadhead_pct > 15 ? 'high' : b.deadhead_pct > 10 ? 'medium' : 'low'}`}>
                              {formatNumber(b.deadhead_pct, 1)}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>
              )}

              {summaryView === 'charts' && (() => {
                const grid = getWeeklyGrid();
                // Top 20 brokers sorted descending by revenue (for miles chart)
                const milesData = [...(summaryData?.summary || [])]
                  .sort((a, b) => (b.total_miles || 0) - (a.total_miles || 0))
                  .slice(0, 20);
                return (
                <div>
                  {/* Per-Week Broker Analysis - matching PDF layout */}
                  {grid && grid.weeks.map(w => {
                    const weekBrokers = grid.brokers
                      .map(b => {
                        const cell = grid.lookup[`${b}|${w}`];
                        return cell ? { ...cell, broker: b } : null;
                      })
                      .filter(Boolean)
                      .sort((a, b) => (b.total_pay || 0) - (a.total_pay || 0))
                      .slice(0, 20);
                    if (!weekBrokers.length) return null;
                    const weekTotal = weekBrokers.reduce((s, b) => s + (b.total_pay || 0), 0);
                    const weekMiles = weekBrokers.reduce((s, b) => s + (b.total_miles || 0), 0);
                    const weekAvgRpm = weekMiles > 0 ? weekTotal / weekMiles : 0;
                    const totalLoads = weekBrokers.reduce((s, b) => s + (b.load_count || 0), 0);

                    return (
                      <div key={w} className="ba-week-section">
                        <div className="ba-week-header-banner">
                          <h3 style={{ color: '#1a4d8f', fontSize: '0.9rem', fontWeight: 700, margin: 0 }}>{formatWeekRange(w)}</h3>
                        </div>
                        <div className="ba-kpi-row" style={{ marginBottom: 12 }}>
                          <div className="kpi-card" style={{ border: '2px solid #1a4d8f' }}>
                            <div className="kpi-label">Total Rate</div>
                            <div className="kpi-value">{weekTotal >= 1000 ? '$' + (weekTotal / 1000).toFixed(2) + 'K' : formatCurrency(weekTotal)}</div>
                          </div>
                          <div className="kpi-card" style={{ border: '2px solid #1a4d8f' }}>
                            <div className="kpi-label">Average Rpm</div>
                            <div className="kpi-value">${formatNumber(weekAvgRpm, 2)}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', fontWeight: 700, color: '#c0392b', fontSize: '0.85rem', marginBottom: 6 }}>Top 20 Broker Analysis</div>
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <ResponsiveContainer width="100%" height={550}>
                              <ComposedChart data={weekBrokers} margin={{ top: 25, right: 50, left: 10, bottom: 90 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                <XAxis dataKey="broker" tick={{ fontSize: 9, angle: -45, textAnchor: 'end' }} interval={0} height={90} />
                                <YAxis yAxisId="left" tickFormatter={v => '$' + (v / 1000).toFixed(0) + 'K'} tick={{ fontSize: 10 }} />
                                <YAxis yAxisId="right" orientation="right" tickFormatter={v => '$' + v.toFixed(1)} tick={{ fontSize: 10 }} domain={[0, 'auto']} />
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const d = payload[0]?.payload;
                                    return (
                                      <div className="ba-chart-tooltip">
                                        <div className="ba-chart-tooltip-name">{d?.broker}</div>
                                        <div>Revenue: {formatCurrency(d?.total_pay)}</div>
                                        <div>Loads: {d?.load_count}</div>
                                        <div>RPM: ${formatNumber(d?.rpm, 2)}</div>
                                      </div>
                                    );
                                  }}
                                />
                                <Legend wrapperStyle={{ fontSize: '0.68rem' }} />
                                <Bar yAxisId="left" dataKey="total_pay" name="Rate" fill="#1a2850" radius={[2, 2, 0, 0]}
                                  label={({ x, y, width, value }) => (
                                    <text x={x + width / 2} y={y - 4} textAnchor="middle" fontSize={8} fontWeight={600} fill="#333">
                                      ${(value / 1000).toFixed(0)}K
                                    </text>
                                  )}
                                />
                                <Line yAxisId="right" dataKey="rpm" name="Rpm" stroke="#16a34a" strokeWidth={2}
                                  dot={{ r: 4, fill: '#16a34a' }}
                                  label={({ x, y, value }) => (
                                    <text x={x} y={y - 10} textAnchor="middle" fontSize={9} fontWeight={700} fill="#16a34a">
                                      ${formatNumber(value, 2)}
                                    </text>
                                  )}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                          <div style={{ minWidth: 180, maxWidth: 220 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.7rem', color: '#1a4d8f', textTransform: 'uppercase', marginBottom: 6 }}>Broker / Loads</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.68rem' }}>
                              <tbody>
                                {weekBrokers.map((b, i) => (
                                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '2px 4px', fontWeight: i < 3 ? 700 : 400, color: i < 3 ? '#c0392b' : 'var(--text-secondary)' }}>{b.broker}</td>
                                    <td style={{ padding: '2px 4px', textAlign: 'right', fontWeight: 600 }}>{b.load_count}</td>
                                  </tr>
                                ))}
                                <tr style={{ borderTop: '2px solid #1a4d8f', fontWeight: 700 }}>
                                  <td style={{ padding: '3px 4px' }}>Total</td>
                                  <td style={{ padding: '3px 4px', textAlign: 'right' }}>{totalLoads}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Broker Weekly Matrix */}
                  {grid && grid.weeks.length > 0 && (
                    <div className="ba-weekly-grid" style={{ marginBottom: 16 }}>
                      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                        <div className="ba-section-header">Broker Breakdown by Week</div>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="ba-matrix-table">
                          <thead>
                            <tr>
                              <th className="ba-matrix-sticky">Broker</th>
                              {grid.weeks.map(w => (
                                <th key={w} className="ba-matrix-week-header">
                                  <span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-secondary)' }}>{formatWeekLabel(w)}</span>
                                  <span style={{ display: 'block', fontSize: '0.55rem', color: 'var(--text-muted)' }}>{formatWeekRange(w)}</span>
                                </th>
                              ))}
                              <th className="ba-matrix-week-header" style={{ background: 'var(--bg-root)' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grid.brokers
                              .sort((a, b) => (grid.brokerTotals[b]?.total_pay || 0) - (grid.brokerTotals[a]?.total_pay || 0))
                              .slice(0, 30)
                              .map(broker => {
                                const bt = grid.brokerTotals[broker];
                                return (
                                  <tr key={broker}>
                                    <td className="ba-matrix-sticky">
                                      <div style={{ fontWeight: 600, fontSize: '0.72rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={broker}>{broker}</div>
                                      <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>{bt.load_count} loads &middot; ${formatNumber(bt.rpm, 2)}</div>
                                    </td>
                                    {grid.weeks.map(w => {
                                      const cell = grid.lookup[`${broker}|${w}`];
                                      if (!cell) return (
                                        <td key={w} className="ba-matrix-cell-empty">
                                          <span style={{ color: 'var(--border)', fontSize: '0.7rem' }}>&mdash;</span>
                                        </td>
                                      );
                                      const heatClass = getHeatClass(cell.total_pay, grid.maxPayPerWeek[w]);
                                      return (
                                        <td key={w} className={`ba-matrix-cell ${heatClass}`}>
                                          <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-primary)' }}>{formatCurrency(cell.total_pay)}</div>
                                          <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', marginTop: 1 }}>{cell.load_count} loads</div>
                                          <div style={{ display: 'inline-block', fontSize: '0.6rem', fontWeight: 600, marginTop: 2, padding: '1px 4px', borderRadius: 3, background: 'rgba(37,99,235,0.08)', color: 'var(--info)' }}>${formatNumber(cell.rpm, 2)}</div>
                                        </td>
                                      );
                                    })}
                                    <td className="ba-matrix-cell" style={{ background: 'var(--bg-root)' }}>
                                      <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-primary)' }}>{formatCurrency(bt.total_pay)}</div>
                                      <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)', marginTop: 1 }}>{bt.load_count} loads</div>
                                      <div style={{ display: 'inline-block', fontSize: '0.6rem', fontWeight: 600, marginTop: 2, padding: '1px 4px', borderRadius: 3, background: 'rgba(37,99,235,0.08)', color: 'var(--info)' }}>${formatNumber(bt.rpm, 2)}</div>
                                    </td>
                                  </tr>
                                );
                              })}
                            {/* Grand Totals Row */}
                            <tr className="ba-matrix-totals-row">
                              <td className="ba-matrix-sticky" style={{ background: 'var(--bg-root)' }}>
                                <div style={{ fontWeight: 800, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Total</div>
                              </td>
                              {grid.weeks.map(w => {
                                const wt = grid.weekTotals[w];
                                return (
                                  <td key={w} className="ba-matrix-cell" style={{ background: 'var(--bg-root)' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-primary)' }}>{formatCurrency(wt.total_pay)}</div>
                                    <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>{wt.load_count} loads</div>
                                    <div style={{ display: 'inline-block', fontSize: '0.6rem', fontWeight: 600, marginTop: 2, padding: '1px 4px', borderRadius: 3, background: 'rgba(37,99,235,0.08)', color: 'var(--info)' }}>${formatNumber(wt.rpm, 2)}</div>
                                  </td>
                                );
                              })}
                              <td className="ba-matrix-cell" style={{ background: 'var(--bg-root)' }}>
                                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-primary)' }}>{formatCurrency(grid.grandTotal.total_pay)}</div>
                                <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>{grid.grandTotal.load_count} loads</div>
                                <div style={{ display: 'inline-block', fontSize: '0.6rem', fontWeight: 600, marginTop: 2, padding: '1px 4px', borderRadius: 3, background: 'rgba(37,99,235,0.08)', color: 'var(--info)' }}>${formatNumber(grid.grandTotal.rpm, 2)}</div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Miles chart — sorted descending */}
                  {milesData.length > 0 && (
                    <div className="chart-wrapper" style={{ marginTop: 16 }}>
                      <div className="chart-title">Loaded Miles &amp; Empty Miles (Top 20)</div>
                      <ResponsiveContainer width="100%" height={Math.max(300, milesData.length * 28)}>
                        <BarChart layout="vertical" data={milesData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <YAxis dataKey="broker" type="category" width={140} tick={{ fontSize: 10 }} />
                          <XAxis type="number" tickFormatter={v => formatNumber(v)} />
                          <Tooltip formatter={(v, name) => [formatNumber(v), name]} />
                          <Legend />
                          <Bar dataKey="total_miles" name="Loaded Miles" stackId="miles" fill={CHART_COLORS.info}
                            label={({ x, y, width, height, value }) => (
                              <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fontSize={9} fontWeight={600} fill="#fff">{formatNumber(value)}</text>
                            )}
                          />
                          <Bar dataKey="total_empty_miles" name="Empty Miles" stackId="miles" fill={CHART_COLORS.negative} opacity={0.6}
                            label={({ x, y, width, height, value }) => {
                              if (!value || width < 30) return null;
                              return <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle" fontSize={9} fontWeight={600} fill="#fff">{formatNumber(value)}</text>;
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Concentration Risk */}
                  <div className="chart-wrapper" style={{ marginTop: 16 }}>
                    <div className="chart-title">Revenue Concentration</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                      <ResponsiveContainer width="50%" height={280}>
                        <PieChart>
                          <Pie data={concentrationData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110}
                            label={({ name, pct }) => `${name.length > 12 ? name.slice(0, 10) + '..' : name} ${pct.toFixed(0)}%`}
                            labelLine={{ strokeWidth: 1 }}>
                            {concentrationData.map((entry, idx) => (
                              <Cell key={idx} fill={SERIES_PALETTE[idx % SERIES_PALETTE.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ flex: 1 }}>
                        {concentrationData.filter(d => d.pct > 25 && d.name !== 'Others').length > 0 && (
                          <div className="ba-concentration-warning">
                            High concentration: {concentrationData.filter(d => d.pct > 25 && d.name !== 'Others').map(d => d.name).join(', ')} &gt;25% of revenue
                          </div>
                        )}
                        <table className="ba-concentration-table">
                          <thead><tr><th>Broker</th><th className="text-right">Revenue</th><th className="text-right">Share</th></tr></thead>
                          <tbody>
                            {concentrationData.map((d, i) => (
                              <tr key={i} className={d.pct > 25 && d.name !== 'Others' ? 'ba-concentration-high' : ''}>
                                <td><span className="ba-concentration-dot" style={{ background: SERIES_PALETTE[i % SERIES_PALETTE.length] }} />{d.name}</td>
                                <td className="text-right">{formatCurrency(d.value)}</td>
                                <td className="text-right">{formatNumber(d.pct, 1)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })()}
              </>
            )
          )}

          {/* Lane Analysis */}
          {activeSection === 'lanes' && (
            !summaryData?.lanes?.length ? (
              <div className="ba-empty">No lane data available.</div>
            ) : (
              <>
                <div className="ba-table-wrapper">
                  <div className="ba-table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <SortTh sort={laneSort} onSort={toggleSort(setLaneSort)} sortKey="broker">Broker</SortTh>
                          <SortTh sort={laneSort} onSort={toggleSort(setLaneSort)} sortKey="pickup_state">PU State</SortTh>
                          <SortTh sort={laneSort} onSort={toggleSort(setLaneSort)} sortKey="delivery_state">DEL State</SortTh>
                          <SortTh sort={laneSort} onSort={toggleSort(setLaneSort)} sortKey="lane_count" className="text-right">Count</SortTh>
                          <SortTh sort={laneSort} onSort={toggleSort(setLaneSort)} sortKey="avg_pay" className="text-right">Avg Pay</SortTh>
                          <SortTh sort={laneSort} onSort={toggleSort(setLaneSort)} sortKey="avg_miles" className="text-right">Avg Miles</SortTh>
                          <SortTh sort={laneSort} onSort={toggleSort(setLaneSort)} sortKey="rpm" className="text-right">RPM</SortTh>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLanes.map((l, i) => (
                          <tr key={i} className="ba-lane-row" onClick={() => handleLaneClick(l)} title="Click to view loads">
                            <td>{l.broker}</td>
                            <td>{l.pickup_state}</td>
                            <td>{l.delivery_state}</td>
                            <td className="text-right">{l.lane_count}</td>
                            <td className="text-right">{formatCurrency(l.avg_pay)}</td>
                            <td className="text-right">{formatNumber(l.avg_miles)}</td>
                            <td className="text-right">
                              <span className={`ba-rpm-badge ${l.rpm >= 3 ? 'rpm-good' : l.rpm >= 2 ? 'rpm-ok' : l.rpm >= 1.5 ? 'rpm-low' : 'rpm-bad'}`}>
                                ${formatNumber(l.rpm, 2)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Ranked List */}
                <div className="ba-top-brokers" style={{ marginTop: 12 }}>
                  {top20Brokers.map((b, i) => (
                    <div key={i} className="ba-broker-bar-row">
                      <div className="ba-broker-bar-rank">{top20Brokers.length - i}</div>
                      <div className="ba-broker-bar-name">{b.broker}</div>
                      <div className="ba-broker-bar-track">
                        <div className="ba-broker-bar-fill" style={{ width: `${Math.max(2, (b.total_pay / maxBrokerPay) * 100)}%` }} />
                      </div>
                      <div className="ba-broker-bar-value">{formatCurrency(b.total_pay)}</div>
                      <div className="ba-broker-bar-meta">
                        <span>{b.load_count} loads</span>
                        <span className="ba-broker-bar-rpm">${formatNumber(b.rpm, 2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          )}


          {/* Detail Loads Table */}
          {activeSection === 'details' && (
            detailLoading ? (
              <div className="ba-loading">
                <div className="dashboard-spinner"></div>
                <p>Loading loads...</p>
              </div>
            ) : !detailLoads?.loads?.length ? (
              <div className="ba-empty">No loads found for the current filters.</div>
            ) : (
              <>
                {selectedLane && (
                  <div className="ba-lane-filter-banner">
                    <span>Filtered: <strong>{selectedLane.broker}</strong> &mdash; {selectedLane.pickup_state} &rarr; {selectedLane.delivery_state}</span>
                    <button className="btn-secondary btn-small" onClick={clearLaneFilter}>Clear Lane Filter</button>
                  </div>
                )}
                <div className="ba-detail-count">Showing {detailLoads.loads.length} of {detailLoads.total} loads</div>
                <div className="ba-table-wrapper ba-table-scroll">
                  <table className="data-table ba-detail-table">
                    <thead>
                      <tr>
                        <DetailSortTh sortKey="load_id" className="ba-col-loadid">Load ID</DetailSortTh>
                        <DetailSortTh sortKey="broker" className="ba-col-broker">Broker</DetailSortTh>
                        <DetailSortTh sortKey="driver_name" className="ba-col-driver">Driver</DetailSortTh>
                        <DetailSortTh sortKey="truck" className="ba-col-truck">Truck</DetailSortTh>
                        <DetailSortTh sortKey="dispatcher" className="ba-col-dispatcher">Disp</DetailSortTh>
                        <th className="ba-col-location">PU Location</th>
                        <DetailSortTh sortKey="pickup_state" className="ba-col-state">PU St</DetailSortTh>
                        <DetailSortTh sortKey="pickup_date" className="ba-col-date">PU Date</DetailSortTh>
                        <th className="ba-col-location">DEL Location</th>
                        <DetailSortTh sortKey="delivery_state" className="ba-col-state">DEL St</DetailSortTh>
                        <DetailSortTh sortKey="delivery_date" className="ba-col-date">DEL Date</DetailSortTh>
                        <DetailSortTh sortKey="load_pay" className="ba-col-pay text-right">Pay</DetailSortTh>
                        <DetailSortTh sortKey="loaded_mile" className="ba-col-miles text-right">Ld Mi</DetailSortTh>
                        <DetailSortTh sortKey="empty_mile" className="ba-col-miles text-right">Mt Mi</DetailSortTh>
                        <DetailSortTh sortKey="total_miles" className="ba-col-miles text-right">Tot Mi</DetailSortTh>
                        <DetailSortTh sortKey="rpm" className="ba-col-rpm text-right">RPM</DetailSortTh>
                        <DetailSortTh sortKey="deadhead_pct" className="ba-col-dh text-right">DH%</DetailSortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {detailLoads.loads.map(load => (
                        <tr key={load.id}>
                          <td className="ba-load-id ba-cell-truncate">{load.load_id}</td>
                          <td className="ba-cell-truncate" title={load.broker || ''}>{load.broker || '-'}</td>
                          <td className="ba-cell-truncate" title={load.driver_name || ''}>{load.driver_name || '-'}</td>
                          <td className="ba-cell-truncate">{load.truck || '-'}</td>
                          <td className="ba-cell-truncate" title={load.dispatcher || ''}>{load.dispatcher || '-'}</td>
                          <td className="ba-cell-truncate" title={load.pickup_location || ''}>{load.pickup_location || '-'}</td>
                          <td>{load.pickup_state || '-'}</td>
                          <td>{formatDate(load.pickup_date)}</td>
                          <td className="ba-cell-truncate" title={load.delivery_location || ''}>{load.delivery_location || '-'}</td>
                          <td>{load.delivery_state || '-'}</td>
                          <td>{formatDate(load.delivery_date)}</td>
                          <td className="text-right">{formatCurrency(load.load_pay)}</td>
                          <td className="text-right">{formatNumber(load.loaded_mile)}</td>
                          <td className="text-right">{formatNumber(load.empty_mile)}</td>
                          <td className="text-right">{formatNumber(load.total_miles)}</td>
                          <td className="text-right ba-rpm">${formatNumber(load.rpm, 2)}</td>
                          <td className="text-right">
                            <span className={`ba-deadhead ${load.deadhead_pct > 15 ? 'high' : load.deadhead_pct > 10 ? 'medium' : 'low'}`}>
                              {formatNumber(load.deadhead_pct, 1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {detailLoads.total_pages > 1 && (
                  <div className="ba-pagination">
                    <button onClick={() => setDetailPage(p => Math.max(1, p - 1))} disabled={detailPage <= 1} className="btn-secondary btn-small">Prev</button>
                    <span>Page {detailPage} of {detailLoads.total_pages}</span>
                    <button onClick={() => setDetailPage(p => Math.min(detailLoads.total_pages, p + 1))} disabled={detailPage >= detailLoads.total_pages} className="btn-secondary btn-small">Next</button>
                  </div>
                )}
              </>
            )
          )}
        </>
      )}
    </div>
  );
}

export default BrokerAnalysis;
